import { useLayout } from "@/lib/preFetch/preFetchLayout";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, Link, SiteCheck, SiteDisable, VisuallyHidden, useI18n, } from "@jog/react-components";
import { checkForTimeElapsed, getUserCountry, typedBoolean } from "@jog/shared";
import { asLink } from "@prismicio/helpers";
import { useCallback, useEffect, useState } from "react";
import { Basket } from "../Icon/Basket";
const countryCodeMap = {
    BE: "BELGIUM",
    DE: "GERMANY",
    NL: "THE NETHERLANDS",
    FR: "FRANCE",
};
const CountrySwitcherDialog = ({ onClose }) => {
    const [showDialog, setShowDialog] = useState(false);
    const [userCountry, setUserCountry] = useState("");
    const { $t } = useI18n();
    const country = process.env.NEXT_PUBLIC_COUNTRY || "";
    const { layout } = useLayout();
    const init = useCallback(() => {
        getUserCountry()
            .then((r) => { var _a; return ((_a = (r || "")) === null || _a === void 0 ? void 0 : _a.replace(/\s/g, "")) || ""; })
            .then((userCountry) => {
            if (userCountry !== country) {
                setUserCountry(userCountry);
                setShowDialog(true);
            }
        });
    }, [country, setShowDialog]);
    useEffect(() => {
        if (checkForTimeElapsed("countryRedirectModal", 7) && !userCountry) {
            init();
        }
        else {
            onClose();
        }
    }, [init, onClose, userCountry]);
    const suggestedCountryCode = Object.keys(countryCodeMap).includes(userCountry) ? userCountry : "US";
    const suggestedCountry = layout.localeSwitch.find((site) => site.countryCode === suggestedCountryCode);
    if (!suggestedCountry)
        return null;
    return (<Dialog open={showDialog} onOpenChange={(v) => {
            setShowDialog(v);
            if (!v) {
                localStorage.setItem("countryRedirectModal", new Date().toISOString());
                onClose();
            }
        }}>
            <DialogContent className="w-[90vw] px-2 py-6 lg:min-w-[300px] lg:max-w-[800px] lg:py-8 lg:px-5">
                <DialogTitle className="mb-5 pt-1 text-center font-primary text-sm font-bold uppercase lg:text-26">{`${$t(`NOT IN`)} ${$t(countryCodeMap[country])}?`}</DialogTitle>
                <DialogDescription>
                    <p className="text-center text-xs lg:text-sm">
                        {$t("You are about to buy products in another country. Please note that the purchase will not be available.")}
                    </p>
                    <p className="text-center text-sm">
                        {$t("We have a webshop in your country that you might prefer to shop instead.")}
                    </p>
                </DialogDescription>
                <div className="flex justify-center">
                    <Link className="mt-2 flex whitespace-nowrap border-none bg-black py-2.5 px-12.5 text-xs uppercase text-white no-underline focus:outline-none lg:mt-7.5 lg:px-7.5" href={asLink(suggestedCountry.link)}>
                        {$t("CONTINUE")} {$t(suggestedCountry.name)}
                    </Link>
                </div>
            </DialogContent>
        </Dialog>);
};
const GlobalSwitcherDialog = ({ onClose }) => {
    const [showDialog, setShowDialog] = useState(false);
    const { $t } = useI18n();
    const layout = useLayout();
    const init = useCallback(() => {
        getUserCountry()
            .then((r) => { var _a; return ((_a = (r || "")) === null || _a === void 0 ? void 0 : _a.replace(/\s/g, "")) || ""; })
            .then((userCountry) => {
            if (Object.keys(countryCodeMap).includes(userCountry)) {
                setShowDialog(true);
            }
        });
    }, []);
    useEffect(() => {
        if (checkForTimeElapsed("countryRedirectModal", 7)) {
            init();
        }
        else {
            onClose();
        }
    }, [init, onClose]);
    return (<Dialog open={showDialog} onOpenChange={(v) => {
            setShowDialog(v);
            if (!v) {
                localStorage.setItem("countryRedirectModal", new Date().toISOString());
                onClose();
            }
        }}>
            <DialogContent className="w-[90vw] px-2 py-6 lg:min-w-[300px] lg:max-w-[800px] lg:py-8 lg:px-5">
                <DialogTitle className="mb-2 p-1 text-center font-primary text-sm font-bold uppercase lg:mb-5 lg:text-26">
                    {$t("Select country")}
                </DialogTitle>
                <DialogDescription>
                    <VisuallyHidden />
                </DialogDescription>
                <div className="grid w-full grid-cols-1 grid-rows-3 gap-3.75 lg:grid-cols-3">
                    {layout.layout.localeSwitch.map((country, index) => {
            const link = asLink(country.link);
            const isCurrentCountry = process.env.NEXT_PUBLIC_SITE_URL
                ? new URL(link).hostname === new URL(process.env.NEXT_PUBLIC_SITE_URL).hostname
                : false;
            const text = [country.name, country.locale].filter(typedBoolean).join(" - ");
            return (<div key={index} className="relative flex items-center justify-center">
                                {isCurrentCountry ? (<DialogClose className="absolute inset-0" aria-label={`${text} is current site, close dialog to continue shopping`}/>) : (<a className="absolute inset-0" href={link} aria-label={`Go to ${text}`}/>)}
                                <span className="mr-2 flex items-center justify-center">
                                    <Basket className="text-gray-icon" width={14} height={14}/>
                                </span>
                                <span className="text-center text-13 text-black no-underline">{text}</span>
                            </div>);
        })}
                </div>
                <p className="mt-3 text-center text-sm leading-tight lg:mt-8.75">
                    {$t("Please select your country and preferred language. When your country is not on the list, it is unfortunately not possible to place an order.")}
                </p>
            </DialogContent>
        </Dialog>);
};
export const CountryCheck = ({ onClose }) => (<>
        <SiteCheck country="US">
            <GlobalSwitcherDialog onClose={onClose}/>
        </SiteCheck>
        <SiteDisable country="US">
            <CountrySwitcherDialog onClose={onClose}/>
        </SiteDisable>
    </>);
